// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type FC, useMemo } from 'react';
import { formatDate } from '@shared/ui-kit/utils/format-date';
import { useAppSelector } from '@advisor-app/store/store';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';

export interface LastUpdatedInfoProps {
  readonly data: Array<{
    last_update?: string;
    subRows?: Array<{ last_update?: string }>;
  }>;
}

export const LastUpdatedInfo: FC<LastUpdatedInfoProps> = ({ data }) => {
  const currency = useAppSelector(selectAccountCurrency);
  const { t, i18n } = useTranslation();

  const lastUpdated = useMemo(() => {
    const firstItemMain = data[0]?.last_update;
    const hasSubRows = !!data[0]?.subRows;

    if (hasSubRows) {
      const firstItemSub = data[0]?.subRows?.[0]?.last_update;
      return firstItemSub && firstItemSub !== 'missing'
        ? firstItemSub
        : data[0]?.subRows?.find(i => i.last_update !== 'missing')?.last_update;
    }

    return firstItemMain && firstItemMain !== 'missing'
      ? firstItemMain
      : data.find(i => i.last_update !== 'missing')?.last_update;
  }, [data]);

  const lastUpdatedDate = useMemo(
    () => formatDate(lastUpdated as string, 'long', i18n.language),
    [lastUpdated, i18n.language],
  );

  return lastUpdated ? (
    <p className="absolute -top-8 right-0 italic text-primary">
      {
        t('glossary.data-as-of', {
          ns: 'glossary',
          lastUpdatedDate,
          currency,
        }) as string
      }
    </p>
  ) : null;
};
